import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, frDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Plus, Receipt, Paperclip, Clock, Wallet, CheckCircle2 } from "lucide-react";
import { KpiStrip } from "@/components/Widget";
import ExpenseExport from "@/components/expenses/ExpenseExport";
import ExpenseForm from "@/components/expenses/ExpenseForm";
import ExpenseDetail, { StatusPill, EXPENSE_STATUS } from "@/components/expenses/ExpenseDetail";

function Summary({ list, filter, setFilter }) {
  const sum = (s) => list.filter((e) => e.status === s).reduce((a, e) => a + (e.amount_ttc || 0), 0);
  const cards = [["envoyee", "À valider", Clock, "#FF6A00"], ["validee", "À rembourser", Wallet, "#FFD500"], ["remboursee", "Remboursé", CheckCircle2, "#34d399"]];
  return <KpiStrip testid="expense-kpis" items={cards.map(([k, l, icon, tint]) => ({ icon, tint, label: l, value: eur(sum(k)), testid: `expense-sum-${k}`, active: filter === k, onClick: () => setFilter(filter === k ? "tous" : k) }))} />;
}

function ExpenseRow({ e, isAdmin, onOpen }) {
  return (
    <button data-testid={`expense-row-${e.id}`} onClick={() => onOpen(e)} className="flex w-full items-center gap-4 rounded-xl border border-zinc-800 bg-zinc-900 p-4 text-left transition-colors hover:border-[#FF6A00]/50">
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 truncate font-medium text-white">{e.vendor}{e.file_id && <Paperclip className="h-3.5 w-3.5 text-zinc-500" />}</p>
        <p className="truncate text-xs text-zinc-500">{isAdmin ? `${e.user_name} · ` : ""}{e.event_title || "Frais généraux"} · {e.date ? frDate(e.date) : frDate(e.created_at)}</p>
      </div>
      <span className="font-display font-semibold text-[#FF6A00]">{eur(e.amount_ttc)}</span>
      <StatusPill status={e.status} />
    </button>
  );
}

export default function Expenses() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ events: [], members: [], is_admin: false });
  const [filter, setFilter] = useState("tous");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const load = () => api.get("/expenses").then(({ data }) => { setList(data); setDetail((d) => (d ? data.find((x) => x.id === d.id) || null : d)); });
  useEffect(() => { load(); api.get("/expenses/meta").then(({ data }) => setMeta(data)); }, []);
  const shown = filter === "tous" ? list : list.filter((e) => e.status === filter);
  const openNew = () => { setEditing(null); setFormOpen(true); };
  return (
    <div className="space-y-6" data-testid="expenses-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-white sm:text-4xl">Notes de frais</h1>
          <p className="mt-2 text-sm text-zinc-500">{meta.is_admin ? "Toutes les notes de l'équipe : validez, refusez et suivez les remboursements." : "Déposez vos justificatifs et suivez le traitement de vos notes."}</p>
        </div>
        <Button data-testid="expense-new" onClick={openNew} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><Plus className="mr-1 h-4 w-4" />Nouvelle note</Button>
      </div>
      <Summary list={list} filter={filter} setFilter={setFilter} />
      {meta.is_admin && <ExpenseExport />}
      <div className="flex flex-wrap gap-2">
        {[["tous", "Toutes"], ...Object.entries(EXPENSE_STATUS).map(([k, [l]]) => [k, l])].map(([k, l]) => (
          <button key={k} data-testid={`expense-filter-${k}`} onClick={() => setFilter(k)}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${filter === k ? "border-[#FF6A00] bg-[#FF6A00]/10 text-[#FF6A00]" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>
            {l} <span className="text-xs opacity-60">{k === "tous" ? list.length : list.filter((e) => e.status === k).length}</span>
          </button>
        ))}
      </div>
      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-800 py-14 text-center" data-testid="expense-empty">
          <Receipt className="mx-auto h-10 w-10 text-zinc-700" />
          <p className="mt-3 text-zinc-500">Aucune note de frais ici.</p>
        </div>
      ) : <div className="space-y-2">{shown.map((e) => <ExpenseRow key={e.id} e={e} isAdmin={meta.is_admin} onOpen={setDetail} />)}</div>}
      <ExpenseForm open={formOpen} onOpenChange={setFormOpen} editing={editing} meta={meta} onSaved={load} />
      <ExpenseDetail e={detail} isAdmin={meta.is_admin} meId={user?.id} onClose={() => setDetail(null)} onChanged={load}
        onEdit={(e) => { setDetail(null); setEditing(e); setFormOpen(true); }} />
    </div>
  );
}
